// Lightweight, dependency-free renderer for the markdown-ish text the LLM
// streams back: paragraphs, "-"/"*" bullets, "1." numbered items, **bold**
// and `code`. Rendered as React nodes (never innerHTML) so model output can
// not inject markup.

import { Fragment, type ReactNode } from 'react';

const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

function renderInline(text: string): ReactNode[] {
    return text.split(INLINE_RE).map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
            return (
                <strong key={i} className="font-semibold text-slate-900 dark:text-white">
                    {part.slice(2, -2)}
                </strong>
            );
        }
        if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
            return (
                <code
                    key={i}
                    className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-[0.85em] text-indigo-700 dark:text-indigo-300"
                >
                    {part.slice(1, -1)}
                </code>
            );
        }
        return <Fragment key={i}>{part}</Fragment>;
    });
}

const BULLET_RE = /^\s*[-*•]\s+(.*)$/;
const NUMBERED_RE = /^\s*(\d+)[.)]\s+(.*)$/;

export const FormattedAnswer = ({ text }: { text: string }) => {
    const lines = text.split('\n');
    return (
        <div className="space-y-1.5">
            {lines.map((line, idx) => {
                if (!line.trim()) return <div key={idx} className="h-1.5" aria-hidden />;
                const bullet = BULLET_RE.exec(line);
                if (bullet) {
                    return (
                        <div key={idx} className="flex gap-2 pl-1">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                            <span>{renderInline(bullet[1])}</span>
                        </div>
                    );
                }
                const numbered = NUMBERED_RE.exec(line);
                if (numbered) {
                    return (
                        <div key={idx} className="flex gap-2 pl-1">
                            <span className="shrink-0 font-mono text-xs font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                                {numbered[1]}.
                            </span>
                            <span>{renderInline(numbered[2])}</span>
                        </div>
                    );
                }
                const heading = /^#{1,6}\s+(.*)$/.exec(line);
                if (heading) {
                    return (
                        <p key={idx} className="font-bold text-slate-900 dark:text-white pt-1">
                            {renderInline(heading[1])}
                        </p>
                    );
                }
                return <p key={idx}>{renderInline(line)}</p>;
            })}
        </div>
    );
};
